<script setup lang="ts">
    import { inject, ref, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormInputTel from '@shared/components/Form/FormInputTel.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import MissingValue from '@shared/components/MissingValue.vue';
    import Speakers from '@views/Event/Speakers.vue';
    import EventEditorDetailsNav from '@views/Event/EventEditor/Details/EventEditorDetailsNav.vue';
    import { getSpeaker } from '@views/Event/Event.access';
    import { type Event, type Speaker } from '@views/Event/EventTypes';
    import NavIcons from '@cwd/Navigation/NavIcons.vue';
    import { eventKey, saveEventPropertyIfChangedKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;
    const saveEventPropertyIfChanged = inject(saveEventPropertyIfChangedKey)!;

    const organizer = ref('');
    const email = ref('');
    const phone = ref('');
    const organizerSpeaker = ref<Speaker | null>(null);
    const showSpeakerModal = ref(false);

    watch(
        () => event.value?.Organizer,
        (value) => (organizer.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.Email,
        (value) => (email.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.Phone,
        (value) => (phone.value = value ?? ''),
        { immediate: true }
    );

    watch(
        () => event.value?.OrganizerSpeakerId,
        async (speakerId) => {
            if (!speakerId) {
                organizerSpeaker.value = null;
                return;
            }

            const speakers = await getSpeaker(speakerId);
            organizerSpeaker.value = speakers[0] ?? null;
        },
        { immediate: true }
    );

    async function onSpeakerSelected(speaker: Speaker) {
        showSpeakerModal.value = false;
        if (!(await saveEventProperty('OrganizerSpeakerId', speaker.SpeakerId, $Resources.EventContactPersonLabel))) return;
        await Promise.all([
            saveEventProperty('Organizer', null, $Resources.EventContactPersonLabel),
            saveEventProperty('Email', null, $Resources.EventContactPersonEmailLabel),
            saveEventProperty('Phone', null, $Resources.EventContactPersonPhoneLabel),
        ]);
        applySpeakerToEvent(speaker);
    }

    function onSpeakerUpdated(speaker: Speaker) {
        if (speaker.SpeakerId === event.value?.OrganizerSpeakerId) {
            organizerSpeaker.value = speaker;
            applySpeakerToEvent(speaker);
        }
    }

    // The server projects the organizer speaker's contact details into Organizer/Email/Phone (EventMapper),
    // so mirror that locally to keep the dashboard in sync without a reload.
    function applySpeakerToEvent(speaker: Speaker | null) {
        if (!event.value) return;
        event.value.Organizer = speaker?.Name ?? null;
        event.value.Email = speaker?.Email ?? null;
        event.value.Phone = speaker?.Phone ?? null;
    }

    async function clearOrganizerSpeaker() {
        if (!(await saveEventProperty('OrganizerSpeakerId', null, $Resources.EventContactPersonLabel))) return;
        await Promise.all([
            saveEventProperty('Organizer', null, $Resources.EventContactPersonLabel),
            saveEventProperty('Email', null, $Resources.EventContactPersonEmailLabel),
            saveEventProperty('Phone', null, $Resources.EventContactPersonPhoneLabel),
        ]);
        applySpeakerToEvent(null);
    }
</script>

<template>
    <div v-if="event" class="max-900">
        <h2 class="h3 m-0-t m-xs-b">{{ $Resources.EventWhoToContactLabel }}</h2>
        <p>{{ $Resources.EventContactPersonAlert }}</p>

        <!--
        <BsAlert :type="AlertTypes.Info" :dismissible="false" :content="$Resources.EventContactPersonAlert" />
        -->

        <div v-if="event.OrganizerSpeakerId" class="m-sm-b">
            <div class="circle spons-image d-inline-block m-sm-b">
                <div class="responsive-square circle">
                    <img :src="organizerSpeaker?.PhotoUrl || '/content/img/icons_svg/avatar.svg'" :alt="organizerSpeaker?.Name" class="img-cover" />
                </div>
            </div>

            <div class="row">
                <div class="col-md-4">
                    <p>
                        <span class="label-look d-block">{{ $Resources.EventContactPersonLabel }}:</span>
                        <template v-if="organizerSpeaker?.Name">{{ organizerSpeaker?.Name }}</template>
                        <MissingValue v-else />
                    </p>
                </div>
                <div class="col-md-4">
                    <p>
                        <span class="label-look d-block">{{ $Resources.EventContactPersonEmailLabel }}:</span>
                        <template v-if="organizerSpeaker?.Email">{{ organizerSpeaker.Email }}</template>
                        <MissingValue v-else />
                    </p>
                </div>
                <div class="col-md-4">
                    <p>
                        <span class="label-look d-block">{{ $Resources.EventContactPersonPhoneLabel }}:</span>
                        <template v-if="organizerSpeaker?.Phone">{{ organizerSpeaker.Phone }}</template>
                        <MissingValue v-else />
                    </p>
                </div>
            </div>
        </div>

        <div v-else class="row">
            <div class="col-md-4">
                <FormInput
                    class="form-group"
                    :label="$Resources.EventContactPersonLabel"
                    :disabled="!canEdit"
                    v-model="organizer"
                    @onBlur="saveEventPropertyIfChanged('Organizer', organizer, $Resources.EventContactPersonLabel)" />
            </div>
            <div class="col-md-4">
                <FormInput
                    class="form-group"
                    type="email"
                    :label="$Resources.EventContactPersonEmailLabel"
                    :disabled="!canEdit"
                    v-model="email"
                    @onBlur="saveEventPropertyIfChanged('Email', email, $Resources.EventContactPersonEmailLabel)" />
            </div>
            <div class="col-md-4">
                <FormInputTel
                    class="form-group"
                    type="tel"
                    :label="$Resources.EventContactPersonPhoneLabel"
                    :disabled="!canEdit"
                    v-model="phone"
                    @onBlur="saveEventPropertyIfChanged('Phone', phone, $Resources.EventContactPersonPhoneLabel)" />
            </div>
        </div>

        <div class="form-group d-flex flex-wrap gap-sm">
            <button type="button" class="btn btn-default" v-if="event.OrganizerSpeakerId" :disabled="!canEdit" @click="clearOrganizerSpeaker">
                {{ $Resources.RemoveButton }}
            </button>

            <button type="button" class="btn btn-default" :disabled="!canEdit" @click="showSpeakerModal = true">
                {{ $Resources.EventContactPersonSelect }}
            </button>
        </div>

        <Modal :title="$Resources.EventContactPersonSelect" :show="showSpeakerModal" @close="showSpeakerModal = false" size="xl">
            <template #content>
                <Speakers
                    :addAsContactPerson="true"
                    @addAsContactPerson="onSpeakerSelected"
                    @createdOrUpdatedSpeaker="onSpeakerUpdated"
                    :updateEnabled="canEdit"></Speakers>
            </template>
        </Modal>
    </div>

    <EventEditorDetailsNav v-if="event" :eventId="props.eventId" />
</template>
